/**
 * El calendario de Gestion sin React: que fechas pide cada vista y como se
 * convierte una solicitud en evento de react-big-calendar.
 */
import moment from "moment";

import { colorDeEstado } from "./gestion";
import { AZUL_BIBLIOTECA, ESTADO_SOLICITADA } from "../constants";

export const VISTAS = {
  MES: "month",
  SEMANA: "week",
  DIA: "day",
  AGENDA: "agenda",
};

// La agenda de react-big-calendar muestra 30 dias desde la fecha elegida.
const DIAS_AGENDA = 30;

const FORMATO = "YYYY-MM-DD";

/**
 * Las fechas que ocupa la vista: el mes con las semanas que lo completan, la
 * semana, el dia o los 30 dias de la agenda. Es lo que se le pide al backend,
 * asi que al cambiar de mes o de vista se vuelve a consultar (en AGIL solo se
 * cargaba el mes en curso, y los demas salian vacios).
 *
 * @returns {{ desde: string, hasta: string }} en YYYY-MM-DD.
 */
export const rangoDeVista = (fecha, vista) => {
  const dia = moment(fecha);
  const [desde, hasta] = {
    [VISTAS.MES]: () => [
      dia.clone().startOf("month").startOf("week"),
      dia.clone().endOf("month").endOf("week"),
    ],
    [VISTAS.SEMANA]: () => [dia.clone().startOf("week"), dia.clone().endOf("week")],
    [VISTAS.DIA]: () => [dia.clone(), dia.clone()],
    [VISTAS.AGENDA]: () => [dia.clone(), dia.clone().add(DIAS_AGENDA - 1, "days")],
  }[vista]();
  return { desde: desde.format(FORMATO), hasta: hasta.format(FORMATO) };
};

const enFecha = (fecha, hora) => moment(`${fecha} ${hora}`, `${FORMATO} HH:mm`).toDate();

/**
 * Una solicitud como evento. Sin horas (el ARIA que pide un estudiante con
 * fecha propuesta) va como evento de todo el dia, "por programar".
 * En `solicitud` queda todo lo que manda el backend.
 */
export const aEvento = (solicitud) => {
  const { fecha, hora_inicio, hora_fin, tipo, materia, solicitante } = solicitud;
  const sinHora = !hora_inicio || !hora_fin;
  const quien = materia || solicitante;
  return {
    id: solicitud.id,
    title: sinHora
      ? `${tipo.nombre} · Por programar · ${quien}`
      : `${tipo.nombre} · ${quien}`,
    start: sinHora ? moment(fecha, FORMATO).toDate() : enFecha(fecha, hora_inicio),
    end: sinHora ? moment(fecha, FORMATO).toDate() : enFecha(fecha, hora_fin),
    allDay: sinHora,
    solicitud,
  };
};

/** Texto al pasar el mouse: lo que no cabe en el evento. */
export const detalleDeEvento = ({ solicitud }) =>
  [
    `${solicitud.tipo.nombre} · ${solicitud.estado.nombre}`,
    solicitud.hora_inicio
      ? `${solicitud.hora_inicio} a ${solicitud.hora_fin}`
      : "Fecha propuesta, sin hora",
    `Solicitante: ${solicitud.solicitante}`,
    solicitud.materia && `Materia: ${solicitud.materia}`,
    solicitud.lugar && `Lugar: ${solicitud.lugar}`,
    solicitud.auxiliares.length
      ? `Auxiliares: ${solicitud.auxiliares.map((a) => `${a.nombre} (${a.accion})`).join(", ")}`
      : "Sin auxiliares",
  ]
    .filter(Boolean)
    .join("\n");

/** El color del evento es el de su estado, el mismo de las tarjetas. */
/**
 * El color de un estado en el calendario: el de las tablas, salvo las
 * solicitadas, que alli son gris claro y en el calendario se pierden contra
 * el fondo; aqui van en el azul del modulo (2026-10-15).
 */
export const colorEnCalendario = (codigo) =>
  codigo === ESTADO_SOLICITADA
    ? { backgroundColor: AZUL_BIBLIOTECA, color: "white" }
    : colorDeEstado(codigo);

export const estiloDeEvento = ({ solicitud }) => {
  const { backgroundColor, color } = colorEnCalendario(solicitud.estado.codigo);
  return { style: { backgroundColor, color, border: "none" } };
};

export const MENSAJES = {
  date: "Fecha",
  time: "Hora",
  event: "Solicitud",
  allDay: "Todo el día",
  week: "Semana",
  work_week: "Semana laboral",
  day: "Día",
  month: "Mes",
  previous: "Anterior",
  next: "Siguiente",
  yesterday: "Ayer",
  tomorrow: "Mañana",
  today: "Hoy",
  agenda: "Agenda",
  noEventsInRange: "No hay solicitudes en estas fechas.",
  showMore: (total) => `+${total} más`,
};
